'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useState } from 'react';
import { COOKIE_CONFIG } from '@//config/cookies';
import {
  type Locale,
  type Market,
  marketLocaleMap,
  supportedLocales,
} from '@//config/domain-locale-map';
import { Selectfield } from '@//containers/Selectfield';

interface LocaleSwapperProps {
  market: Market;
  locale: Locale;
}

const LocaleSwapper = ({ market, locale: _locale }: LocaleSwapperProps) => {
  const router = useRouter();
  const t = useTranslations('other.footer');
  const [locale, setLocale] = useState<Locale>(() => {
    // Initialize the state from the current locale cookie
    const fallbackLocale = marketLocaleMap[market];
    if (typeof document !== 'undefined') {
      const regex = new RegExp(`${COOKIE_CONFIG.LOCALE.name}=([^;]*)`);
      const match = document.cookie.match(regex);
      const extractedLocale = match?.[1]; // Use capture group [1] not [0]
      return extractedLocale && supportedLocales.includes(extractedLocale as Locale)
        ? (extractedLocale as Locale)
        : fallbackLocale; // Default to fallbackLocale if no cookie is set
    }
    return _locale || fallbackLocale;
  });

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLocale = e.target.value;
    setLocale(newLocale as Locale);

    // Set the locale cookie using Cookie Store API (preferred) or fallback
    const maxAge = COOKIE_CONFIG.LOCALE.maxAge;
    const isSecure = window.location.protocol === 'https:';

    try {
      if ('cookieStore' in window && window.cookieStore) {
        await window.cookieStore.set({
          name: COOKIE_CONFIG.LOCALE.name,
          value: newLocale,
          expires: Date.now() + maxAge * 1000,
          path: '/',
          sameSite: 'lax',
          ...(isSecure && { secure: true }),
        });
      } else {
        // Fallback for browsers without Cookie Store API (Firefox, Safari)
        const secureFlag = isSecure ? '; secure' : '';
        document.cookie = `${COOKIE_CONFIG.LOCALE.name}=${newLocale}; max-age=${maxAge}; path=/; samesite=lax${secureFlag}`;
      }
    } catch {
      // Final fallback if Cookie Store API fails
      const secureFlag = isSecure ? '; secure' : '';
      document.cookie = `${COOKIE_CONFIG.LOCALE.name}=${newLocale}; max-age=${maxAge}; path=/; samesite=lax${secureFlag}`;
    }

    router.refresh();
  };

  const selectId = 'language-selector';

  const localeOptions = supportedLocales.map((locale) => ({
    value: locale,
    label: locale,
  }));

  return (
    <Selectfield
      id={selectId}
      label={t('language')}
      ariaLabel={t('language')}
      options={localeOptions}
      value={locale}
      onChange={onChange}
    />
  );
};

export default LocaleSwapper;
